import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { RiArrowLeftLine, RiSendPlane2Fill } from "@remixicon/react";
import EmptyState from "../common/EmptyState";
import useIsMobile from "../../hooks/useIsMobile";
import Loader from "../common/Loader";
import { getChatList, getChatDetails, sendChatMessage } from "../../api/common";
import "./Messages.scss";

export default function Messages() {
	const [searchParams] = useSearchParams();
	const deepLinkUserId = searchParams.get("userId");
	const deepLinkBidRequestId = searchParams.get("bidRequestId");
	const [loading, setLoading] = useState(true);
	const [threads, setThreads] = useState([]);
	const [activeUserId, setActiveUserId] = useState(deepLinkUserId);
	const isMobile = useIsMobile();

	useEffect(() => {
		(async () => {
			setLoading(true);
			const res = await getChatList();
			if (res && !res.error) {
				const list = res.data || [];
				setThreads(list);
				if (!deepLinkUserId && list.length && !window.matchMedia("(max-width: 768px)").matches) setActiveUserId(list[0].userId || list[0]._id || list[0].id);
			}
			setLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (loading) return <Loader />;

	return (
		<div className={`messages-shell${isMobile && activeUserId ? " show-thread" : ""}`}>
			<div className="messages-list">
				<div className="messages-list-head">Messages</div>
				{threads.length === 0 ? (
					<EmptyState title="No conversations yet" subtitle="Messages about your bids and orders will appear here." />
				) : (
					threads.map((t) => {
						const id = t.userId || t._id || t.id;
						return (
							<button
								key={id}
								className={`thread-item ${id === activeUserId ? "active" : ""}`}
								onClick={() => setActiveUserId(id)}
							>
								<div className="thread-top">
									<span className="thread-name">{t.userName || t.name || t.companyName || "Conversation"}</span>
									{t.lastMsgTiming ? <span className="thread-time">{moment(t.lastMsgTiming).format("MMM D, h:mm A")}</span> : null}
								</div>
								<div className="thread-bottom">
									<span className="thread-preview text-muted">{t.lastMsg || t.lastMessage || ""}</span>
									{t.unreadMsgCount > 0 ? <span className="thread-unread">{t.unreadMsgCount}</span> : null}
								</div>
							</button>
						);
					})
				)}
			</div>
			<div className="messages-thread">
				{activeUserId ? (
					<ChatThread
						onBack={isMobile ? () => setActiveUserId(null) : null}
						title={threads.find((t) => (t.userId || t._id || t.id) === activeUserId)?.name || threads.find((t) => (t.userId || t._id || t.id) === activeUserId)?.companyName}
						userId={activeUserId}
						bidRequestId={deepLinkBidRequestId || threads.find((t) => (t.userId || t._id || t.id) === activeUserId)?.bidRequestId}
					/>
				) : (
					<EmptyState title="Select a conversation" />
				)}
			</div>
		</div>
	);
}

function ChatThread({ userId, bidRequestId, onBack, title }) {
	const [loading, setLoading] = useState(true);
	const [messages, setMessages] = useState([]);
	const [text, setText] = useState("");
	const [sending, setSending] = useState(false);
	const bottomRef = useRef(null);

	const load = async () => {
		setLoading(true);
		const res = await getChatDetails(userId, "50", bidRequestId || "");
		if (res && !res.error) setMessages(res.data || []);
		setLoading(false);
	};

	useEffect(() => {
		load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId]);

	useEffect(() => {
		bottomRef.current?.scrollIntoView({ behavior: "smooth" });
	}, [messages]);

	const send = async () => {
		if (!text.trim()) return;
		setSending(true);
		const res = await sendChatMessage(userId, { message: text, bidRequestId });
		setSending(false);
		if (res && !res.error) {
			setText("");
			load();
		}
	};

	if (loading) return <Loader />;

	return (
		<div className="thread-panel">
			{onBack ? (
				<div className="thread-head">
					<button type="button" className="icon-inline" onClick={onBack} aria-label="Back to conversations">
						<RiArrowLeftLine size={18} />
					</button>
					<span className="fw-bold text-truncate">{title || "Conversation"}</span>
				</div>
			) : null}
			<div className="thread-messages">
				{messages.length === 0 ? (
					<EmptyState title="No messages yet" subtitle="Say hello to get the conversation started." />
				) : (
					messages.map((m, idx) => (
						<div key={m.id || m._id || idx} className={`chat-bubble ${m.isMine || m.senderIsBuyer ? "mine" : ""}`}>
							<div className="chat-bubble-text">{m.message || m.text}</div>
							<div className="chat-bubble-time text-muted">{m.createdAt ? moment(m.createdAt).format("MMM D, h:mm A") : ""}</div>
						</div>
					))
				)}
				<div ref={bottomRef} />
			</div>
			<div className="thread-input">
				<input
					className="form-control"
					placeholder="Type a message..."
					value={text}
					onChange={(e) => setText(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && send()}
				/>
				<button className="btn-accent-solid" disabled={sending} onClick={send} aria-label="Send">
					<RiSendPlane2Fill size={16} />
					<span className="d-none d-md-inline">Send</span>
				</button>
			</div>
		</div>
	);
}
